import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { ArrowUpRight, Calendar, Clock } from "lucide-react";
import posts from "../data/blog.json";

const FILTERS = [
  { key: "all", label: "All posts" },
  { key: "candidates", label: "For candidates" },
  { key: "employers", label: "For employers" },
  { key: "market", label: "Market" },
];

const AUDIENCE_LABEL = {
  candidates: "For candidates",
  employers: "For employers",
  market: "Market",
};

export default function Help() {
  const [filter, setFilter] = useState("all");

  const visible = useMemo(() => {
    if (filter === "all") return posts;
    return posts.filter((p) => p.audience === filter);
  }, [filter]);

  const featured = visible[0];
  const rest = visible.slice(1);

  return (
    <div data-testid="page-help">
      <Seo
        title="Help & Insights"
        description="Honest, jargon-free insights on hiring and job-hunting in Aotearoa — written by the consultants who actually do the work."
        image="/og-brand.png"
        path="/help"
      />
      <PageHero
        overline="Help & insights"
        title="Ideas worth reading on hiring and finding work."
        accentWord="Ideas"
        subtitle="Honest, jargon-free pieces on the New Zealand talent market — from the consultants who actually do the work."
      />

      {/* Filter chips */}
      <section className="pb-8 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="inline-flex flex-wrap gap-2 bg-mint-soft p-1.5 rounded-full border border-ink-100" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`help-filter-${f.key}`}
                role="tab"
                aria-selected={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={`px-5 md:px-6 py-2.5 rounded-full text-xs md:text-sm font-body font-medium transition-all ${
                  filter === f.key ? "bg-ink text-mint" : "text-ink hover:text-stone-dark"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="pb-20 md:pb-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          {visible.length === 0 ? (
            <div className="bg-mint-soft rounded-2xl p-12 text-center" data-testid="help-empty-state">
              <p className="font-headings text-2xl text-ink">No posts in this section yet.</p>
              <p className="font-body text-ink-700 mt-2">
                Check another filter — or watch this space, we're publishing regularly.
              </p>
            </div>
          ) : (
            <>
              {/* Featured / most recent post */}
              {featured && (
                <Link
                  to={`/help/${featured.slug}`}
                  data-testid={`help-featured-post`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-stretch group mb-14 md:mb-20"
                >
                  <div className="md:col-span-5 bg-stone rounded-2xl p-10 flex flex-col justify-between min-h-[300px]">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-mint font-medium">
                      Latest · {featured.topic}
                    </span>
                    <div className="mt-8">
                      <p className="font-headings text-6xl md:text-7xl text-mint leading-none">
                        {String(new Date(featured.publishedAt).getDate()).padStart(2, "0")}
                      </p>
                      <p className="mt-2 font-body text-sm uppercase tracking-[0.25em] text-cream/70">
                        {new Date(featured.publishedAt).toLocaleDateString("en-NZ", { month: "short", year: "numeric" })}
                      </p>
                    </div>
                  </div>
                  <article className="md:col-span-7 bg-white rounded-2xl p-8 md:p-12 border border-ink-100 group-hover:shadow-md transition-shadow flex flex-col">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="text-[10px] uppercase tracking-[0.25em] font-medium text-ink bg-mint px-3 py-1 rounded-full">
                        {AUDIENCE_LABEL[featured.audience]}
                      </span>
                      <span className="text-xs text-stone-dark font-body flex items-center gap-1.5">
                        <Clock size={12} strokeWidth={1.5} /> {featured.readingMinutes} min read
                      </span>
                    </div>
                    <h2 className="font-headings text-3xl md:text-4xl text-ink leading-tight group-hover:text-stone-dark transition-colors">
                      {featured.title}
                    </h2>
                    <p className="font-body text-ink-700 leading-relaxed flex-1 mt-5 text-lg">
                      {featured.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-2 mt-8 font-body font-medium text-ink">
                      Read the post <ArrowUpRight size={16} />
                    </span>
                  </article>
                </Link>
              )}

              {/* Rest of the posts */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="help-post-grid">
                  {rest.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/help/${p.slug}`}
                      data-testid={`help-post-${p.slug}`}
                      className="bg-white p-8 md:p-10 rounded-2xl border border-ink-100 hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer group flex flex-col"
                    >
                      <div className="flex items-center gap-3 mb-5">
                        <span className="text-[10px] uppercase tracking-[0.25em] font-medium text-ink bg-mint px-3 py-1 rounded-full">
                          {AUDIENCE_LABEL[p.audience]}
                        </span>
                        <span className="text-xs text-stone-dark font-body flex items-center gap-1.5">
                          <Clock size={12} strokeWidth={1.5} /> {p.readingMinutes} min
                        </span>
                      </div>
                      <h3 className="font-headings text-2xl md:text-3xl text-ink leading-tight group-hover:text-stone-dark transition-colors mb-4">
                        {p.title}
                      </h3>
                      <p className="font-body text-ink-700 leading-relaxed flex-1">{p.excerpt}</p>
                      <div className="mt-6 flex items-center justify-between text-sm">
                        <span className="text-stone-dark flex items-center gap-1.5">
                          <Calendar size={12} strokeWidth={1.5} />
                          {new Date(p.publishedAt).toLocaleDateString("en-NZ", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                        <span className="inline-flex items-center gap-2 font-medium text-ink">
                          Read <ArrowUpRight size={14} />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Newsletter CTA */}
          <div className="mt-16 md:mt-24 bg-mint rounded-2xl p-10 md:p-12 text-center">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark mb-4">
              Want it in your inbox?
            </p>
            <h3 className="font-headings text-3xl md:text-4xl text-ink mb-4 leading-tight">
              One thoughtful email a month. <span className="italic">That's it.</span>
            </h3>
            <p className="font-body text-ink-700 max-w-xl mx-auto mb-6">
              We send a short note with our latest insights, market reads, and
              roles we think you'd find interesting. Unsubscribe any time.
            </p>
            <Link
              to="/contact"
              data-testid="help-newsletter-cta"
              className="inline-flex items-center gap-2 bg-ink text-mint hover:bg-stone-dark px-7 py-3.5 rounded-full font-body font-medium"
            >
              Get in touch →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
